import { useMemo, useRef } from "react";
import { resolveFieldConfig } from "../config/fieldConfig";
import { evaluateCondition } from "../config/fieldConfig/conditionUtils";
import type {
	FieldConfig,
	FieldResolverContext,
	ResolvedFieldConfig,
} from "../config/fieldConfig/types";

/**
 * Filter fields by dependsOn — exclude fields whose condition is not met.
 */
function filterByDependsOn(
	fields: FieldConfig[],
	formValues: Record<string, unknown>,
): FieldConfig[] {
	return fields.filter((field) => {
		if (!field.dependsOn) return true;
		return evaluateCondition(field.dependsOn, formValues);
	});
}

/**
 * React hook wrapping resolveFieldConfig().
 *
 * Memoizes the resolved config — only recomputes when context or formValues change.
 * When formValues is provided, filters fields by dependsOn (e.g. convictionDetails
 * only visible when anyOwnerConvicted === true).
 * Exposes contextChanged for disclosure banners when country/entityType changes.
 */
export function useFieldResolver(
	ctx: FieldResolverContext,
	formValues?: Record<string, unknown>,
): ResolvedFieldConfig & {
	contextChanged: boolean;
} {
	const prevCtxRef = useRef<FieldResolverContext | null>(null);

	const result = useMemo(() => {
		const resolved = resolveFieldConfig(ctx);
		if (!formValues || Object.keys(formValues).length === 0) {
			return resolved;
		}
		const filteredFields = filterByDependsOn(resolved.fields, formValues);
		const fieldMap: Record<string, FieldConfig> = {};
		for (const field of filteredFields) {
			fieldMap[field.fieldId] = field;
		}
		return { ...resolved, fields: filteredFields, fieldMap };
	}, [ctx.incorporationCountry, ctx.entityType, ctx.flowType, formValues, ctx]);

	const contextChanged =
		prevCtxRef.current !== null &&
		(prevCtxRef.current.incorporationCountry !== ctx.incorporationCountry ||
			prevCtxRef.current.entityType !== ctx.entityType);

	prevCtxRef.current = ctx;

	return { ...result, contextChanged };
}
